import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import Tooltip from 'components/application/Tooltip';

import { useProjectsAnalyticsDashboardContext } from 'components/contexts/ProjectsAnalyticsDashboardContext';
import {
  collectDomainCompanies,
  sortCompaniesByPrimaryAndName,
} from 'components/projectsAnalytics/helpers/filters';

export const TOOLTIP_DIVISION_NAME_LIMIT = 20;

export default function ProjectsAnalyticsDivisionsFilterBanner({
  divisionIdsFilter,
}) {
  const { divisionsTotalCount } = useProjectsAnalyticsDashboardContext();

  const queryClient = useQueryClient();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectsAnalytics.components.ProjectsAnalyticsDivisionsFilterBanner',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const filteredDivisionsCount = divisionIdsFilter.size;

  if (filteredDivisionsCount === 0) {
    return (
      <div className='tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-700'>
        {i18nT('allDivisionsWithCount', { count: divisionsTotalCount })}
      </div>
    );
  }

  const collectedDomainCompanies = collectDomainCompanies({ queryClient });

  const filteredDivisions = sortCompaniesByPrimaryAndName({
    companies: [...divisionIdsFilter].map(
      (divisionId) => collectedDomainCompanies[divisionId],
    ),
  });

  const filteredDivisionNames = filteredDivisions.map(
    (division) => division.attributes.name,
  );

  const firstDivisionName = filteredDivisionNames[0];

  if (filteredDivisionsCount === 1) {
    return (
      <div className='tw-text-l tw-font-normal tw-tracking-auto tw-text-grey-700'>
        <Trans
          components={{
            span: <span className='tw-font-semibold' />,
          }}
          i18nKey='projectsAnalytics.components.ProjectsAnalyticsDivisionsFilterBanner.divisionCountAndNames'
          ns='components'
          tOptions={{ interpolation: { escapeValue: false } }}
          values={{
            count: filteredDivisionsCount,
            divisionName: firstDivisionName,
          }}
        />
      </div>
    );
  }

  const moreCount = filteredDivisionsCount - 1;

  const visibleTooltipDivisionNames = filteredDivisionNames.slice(
    1,
    1 + TOOLTIP_DIVISION_NAME_LIMIT,
  );

  const unnamedDivisionCount = moreCount - visibleTooltipDivisionNames.length;

  return (
    <div className='tw-text-l tw-font-normal tw-tracking-auto tw-text-grey-700'>
      <Trans
        components={{
          semiboldSpan: <span className='tw-font-semibold' />,
          underlineSpan: <span className='tw-underline' />,
          tooltip: (
            <Tooltip
              placement='bottom'
              tooltip={
                unnamedDivisionCount === 0 ?
                  visibleTooltipDivisionNames.join(', ')
                : `${visibleTooltipDivisionNames.join(', ')}... ${i18nTShared(
                    'other',
                    { count: unnamedDivisionCount },
                  )}`
              }
              trigger='hover'
            />
          ),
        }}
        i18nKey='projectsAnalytics.components.ProjectsAnalyticsDivisionsFilterBanner.divisionCountAndNames'
        ns='components'
        tOptions={{ interpolation: { escapeValue: false } }}
        values={{
          count: filteredDivisionsCount,
          firstDivisionName,
          moreCount,
        }}
      />
    </div>
  );
}

ProjectsAnalyticsDivisionsFilterBanner.propTypes = {
  divisionIdsFilter: PropTypes.instanceOf(Set).isRequired,
};
